'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import type { Profile } from '@/lib/booking/types';
import { getSupabaseBrowserClient } from '@/lib/supabase/client';

type SignInInput = {
  email: string;
  nextPath?: string;
};

type SignUpInput = SignInInput & {
  fullName: string;
  phone: string;
};

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (payload: SignInInput) => Promise<void>;
  signUp: (payload: SignUpInput) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = useMemo(() => getSupabaseBrowserClient(), []);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  const normalizeNextPath = useCallback((nextPath?: string | null) => {
    if (!nextPath || !nextPath.startsWith('/')) {
      return '/account';
    }
    const allowedPrefixes = ['/admin', '/account', '/book'];
    const isAllowed = allowedPrefixes.some(
      (prefix) =>
        nextPath === prefix ||
        nextPath.startsWith(`${prefix}/`) ||
        nextPath.startsWith(`${prefix}?`)
    );
    return isAllowed ? nextPath : '/account';
  }, []);

  const getEmailRedirectTo = useCallback(
    (nextPath?: string) => {
      if (typeof window === 'undefined') {
        return undefined;
      }
      const safePath = normalizeNextPath(nextPath);
      return `${window.location.origin}${safePath}`;
    },
    [normalizeNextPath]
  );

  const ensureProfile = useCallback(
    async (user: User | null) => {
      if (!supabase || !user) {
        return;
      }

      const fullName =
        typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : null;
      const phone = typeof user.user_metadata?.phone === 'string' ? user.user_metadata.phone : null;

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          phone,
        })
        .eq('id', user.id);

      if (error && process.env.NODE_ENV !== 'production') {
        console.warn('[auth] ensureProfile update failed', {
          userId: user.id,
          error: error.message,
        });
      }
    },
    [supabase]
  );

  const fetchProfile = useCallback(
    async (userId?: string | null) => {
      if (!supabase) {
        setProfile(null);
        return;
      }

      if (!userId) {
        setProfile(null);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, phone, role')
        .eq('id', userId)
        .maybeSingle();

      if (error && process.env.NODE_ENV !== 'production') {
        console.warn('[auth] fetchProfile failed', { userId, error: error.message });
      }

      setProfile((data as Profile | null) ?? null);
    },
    [supabase]
  );

  const syncSessionState = useCallback(
    (nextSession: Session | null) => {
      setSession(nextSession);

      // Let GoTrue finish storage updates before profile queries run.
      queueMicrotask(async () => {
        await ensureProfile(nextSession?.user ?? null);
        await fetchProfile(nextSession?.user.id);
        setLoading(false);
      });
    },
    [ensureProfile, fetchProfile]
  );

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    if (initializedRef.current) {
      return;
    }
    initializedRef.current = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      syncSessionState(nextSession);
    });

    return () => {
      subscription.unsubscribe();
      initializedRef.current = false;
    };
  }, [supabase, syncSessionState]);

  const signIn = useCallback(
    async ({ email, nextPath }: SignInInput) => {
      if (!supabase) {
        throw new Error('Authentication is unavailable. Missing Supabase configuration.');
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false,
          emailRedirectTo: getEmailRedirectTo(nextPath),
        },
      });
      if (error) {
        throw error;
      }
    },
    [getEmailRedirectTo, supabase]
  );

  const signUp = useCallback(
    async ({ email, fullName, phone, nextPath }: SignUpInput) => {
      if (!supabase) {
        throw new Error('Authentication is unavailable. Missing Supabase configuration.');
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
          emailRedirectTo: getEmailRedirectTo(nextPath),
          data: {
            full_name: fullName,
            phone,
          },
        },
      });
      if (error) {
        throw error;
      }
    },
    [getEmailRedirectTo, supabase]
  );

  const signOut = useCallback(async () => {
    if (!supabase) {
      return;
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
  }, [supabase]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      profile,
      loading,
      signIn,
      signUp,
      signOut,
      refreshProfile: () => fetchProfile(session?.user.id),
    }),
    [fetchProfile, loading, profile, session, signIn, signOut, signUp]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
